import {createStore} from 'vuex'
import createPersistedState from "vuex-persistedstate";
import AccessTokenDTO from "@/models/AccessTokenDTO";
import APIClient from "@/service/APIClient";
import UserDTO from "@/models/UserDTO"
import {setLang} from "@/i18n";
import {Currency} from "@/models/enum/Currency";
import LiveWinDTO from "@/models/LiveWinDTO";
import TournamentDTO from "@/models/TournamentDTO";
import GameDTO from "@/models/GameDTO";

const defaultState = {
  liveWins: [] as LiveWinDTO[],
  liveTournaments: [] as TournamentDTO[],
  favoriteGames: [] as GameDTO[],
  lang: null as string|null,
  auth: null as AccessTokenDTO | null,
  user: null as UserDTO | null,
};

const api = new APIClient()
export default createStore({
  state: {
    ...defaultState
  },
  mutations: {
    authenticate(state, token: AccessTokenDTO) {
      state.auth = token;
    },
    setBalance(state, payload: any) {
      if (!state.user) {
        return
      }

      const amount = payload.amount;
      const currency = payload.currency;
      for (const account of state.user!.accounts) {
        if (account.currency !== currency) {
          continue;
        }
        account.availableSum.amount = amount
      }
    },
    setLiveWins(state, liveWins: LiveWinDTO[]) {
      state.liveWins = liveWins
    },
    setLiveTournaments(state, liveTournaments: TournamentDTO[]) {
      state.liveTournaments = liveTournaments
    },
    setFavoriteGames(state, games: GameDTO[]) {
      state.favoriteGames = games
    },
    addFavoriteGame(state, game: GameDTO) {
      state.favoriteGames.unshift(game)
    },
    removeFavoriteGame(state, game: GameDTO) {
      const index = state.favoriteGames.findIndex(g => g.id === game.id)
      state.favoriteGames.splice(index, 1)
    },
    removeAuth(state) {
      Object.assign(state, {...defaultState})
    },
    setUser(state, user: UserDTO | null) {
      state.user = user;
      window.userTimeZone = user?.timezone ?? 'UTC';
      state.lang = window.userLang
    },
  },
  actions: {
    emit(_ctx, payload: { type: string; data?: unknown }) {},
    async login({commit}) {
      try {
        const user = await api.call.users.login()
        commit('setUser', user);
      } catch (e) {

      }
    },
    async updateUser({commit}) {
      try {
        const user = await api.call.users.me()
        commit('setUser', user);
      } catch (e) {

      }
    },
    async joinTournament({commit, dispatch}, tid: number) {
      await api.call.tournaments.join(tid)
      await dispatch('updateUser')
      await commit('setLiveTournaments', await api.call.live.tournaments())
    },
    logout({commit}) {
      commit('removeAuth');
    },
    setBalance({commit}, amount: string) {
      commit('setBalance', {amount, currency: Currency.GOLD_COIN});
    },
    setLiveWins({commit}, liveWins: LiveWinDTO[]) {
      commit('setLiveWins', liveWins);
    },
    setFavoriteGames({commit}, games: GameDTO[]) {
      commit('setFavoriteGames', games);
    },
    removeFavoriteGame({commit}, game: GameDTO) {
      commit('removeFavoriteGame', game);
    },
    addFavoriteGame({commit}, game: GameDTO) {
      commit('addFavoriteGame', game);
    },
    setLiveTournaments({commit}, items: TournamentDTO[]) {
      commit('setLiveTournaments', items);
    },
    authenticate({commit}, accessToken: AccessTokenDTO) {
      commit('authenticate', accessToken);
    },
  },
  getters: {
    liveWins: state => state.liveWins,
    liveTournaments: state => state.liveTournaments,
    favoriteGames: state => state.favoriteGames,
    lang: state => state.lang,
    isLoggedIn: state => state.auth !== null,
    getAuth: state => state.auth,
    getUser: state => state.user,
    getBalance: state => (currency: Currency) => {
      return state.user?.accounts.find((account) => account.currency === currency)?.availableSum.amount ?? '0'
    }
  },
  strict: true,
  plugins: [createPersistedState()],
  modules: {
  }
})
